import { useNavigate } from "react-router-dom";
import { commitDraft, closeDraft, expandDraft, useDraft } from "../app/draft";
import { BOTTOM_RESERVE } from "../app/chrome.js";
import { ChevronUpIcon } from "./icons";
import { useT } from "../i18n/index.js";
import { cn } from "../lib/cn";

/**
 * The line a pushed-down form leaves behind (SPEC §4.11).
 *
 * It sits above the tab bar and does three things, which are the only three
 * things anybody wants from a half-written form they stepped away from: open
 * it again, throw it away, or — when there is already enough in it — save it
 * from here without opening anything.
 *
 * Above the *whole* app rather than on the screen that opened it, because the
 * point of pushing a form down is to go somewhere else: the calendar, to see
 * whether Wednesday is free, and then back.
 */
export function DraftBar() {
  const t = useT();
  const draft = useDraft();
  const navigate = useNavigate();

  if (!draft || !draft.minimised) return null;

  const ready = draft.isReady(draft.values);

  return (
    <div
      className="pad-safe-x pointer-events-none fixed inset-x-0 z-30 mx-auto w-full max-w-2xl px-4"
      style={{ bottom: `var(--chrome-bottom, ${BOTTOM_RESERVE})` }}
    >
      <div className="bg-surface pointer-events-auto flex items-center gap-2 rounded-card px-3 py-2 shadow-soft ring-1 ring-line">
        <button
          type="button"
          onClick={() => {
            // Back to where it belongs first: the sheet is the screen's, and a
            // form reopened over the wrong tab would be a form you cannot see
            // the point of.
            navigate(draft.path);
            expandDraft();
          }}
          aria-label={t("common.expand")}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
        >
          {/* The chevron points back up the way the sheet went down. Without
              it the line reads as a notice rather than as a door. */}
          <ChevronUpIcon
            aria-hidden="true"
            className="text-muted size-5 shrink-0"
            strokeWidth={1.9}
          />
          <span className="min-w-0">
            <span className="block truncate text-sm font-medium">{draft.title}</span>
            <span className="text-muted block text-xs">{t("common.draft")}</span>
          </span>
        </button>

        <button
          type="button"
          onClick={closeDraft}
          className="text-muted shrink-0 rounded-pill px-3 py-2 text-sm"
        >
          {t("common.cancel")}
        </button>

        <button
          type="button"
          onClick={commitDraft}
          disabled={!ready}
          className={cn(
            "shrink-0 rounded-pill px-4 py-2 text-sm font-semibold transition-colors",
            ready ? "bg-accent text-ground" : "bg-surface-alt text-muted",
          )}
        >
          {t("common.save")}
        </button>
      </div>
    </div>
  );
}
